import { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type ActivityWindowInput,
  getActivityProfile,
  replaceActivityProfile,
} from "@/api/activity";
import { listOrganizations } from "@/api/organizations";
import { usePermission } from "@/auth/usePermission";

const WEEKDAYS: number[] = [1, 2, 3, 4, 5, 6, 7];

type LocalWindow = ActivityWindowInput & { _key: string };

function makeKey(): string {
  return Math.random().toString(36).slice(2, 9);
}

export function ActivityProfileTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canWrite = usePermission("organization:update");

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });

  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    if (!orgId && orgs.data?.items.length) setOrgId(orgs.data.items[0]?.id ?? "");
  }, [orgs.data, orgId]);

  const profile = useQuery({
    queryKey: ["activity-profile", orgId],
    queryFn: () => getActivityProfile(orgId),
    enabled: Boolean(orgId),
  });

  const [label, setLabel] = useState("");
  const [windows, setWindows] = useState<LocalWindow[]>([]);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (profile.data) {
      setLabel(profile.data.label);
      setWindows(
        profile.data.windows.map((w) => ({
          weekday: w.weekday,
          start_time: w.start_time.slice(0, 5),
          end_time: w.end_time.slice(0, 5),
          _key: makeKey(),
        })),
      );
    }
  }, [profile.data]);

  function applyPreset(preset: "24_7" | "weekdays" | "weekend" | "clear"): void {
    if (preset === "24_7") {
      setWindows(
        WEEKDAYS.map((d) => ({
          weekday: d,
          start_time: "00:00",
          end_time: "23:59",
          _key: makeKey(),
        })),
      );
    } else if (preset === "weekdays") {
      setWindows(
        [1, 2, 3, 4, 5].map((d) => ({
          weekday: d,
          start_time: "08:00",
          end_time: "20:00",
          _key: makeKey(),
        })),
      );
    } else if (preset === "weekend") {
      setWindows(
        [6, 7].map((d) => ({
          weekday: d,
          start_time: "00:00",
          end_time: "23:59",
          _key: makeKey(),
        })),
      );
    } else {
      setWindows([]);
    }
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      replaceActivityProfile(
        orgId,
        label,
        windows.map((w) => ({
          weekday: w.weekday,
          start_time: w.start_time.length === 5 ? `${w.start_time}:00` : w.start_time,
          end_time: w.end_time.length === 5 ? `${w.end_time}:00` : w.end_time,
        })),
      ),
    onSuccess: () => {
      setSavedMessage(t("activity.saved"));
      setErrorMessage(null);
      qc.invalidateQueries({ queryKey: ["activity-profile", orgId] });
      setTimeout(() => setSavedMessage(null), 3000);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setErrorMessage(typeof detail === "string" ? detail : t("activity.saveError"));
    },
  });

  const windowsByDay = WEEKDAYS.map((d) => ({
    day: d,
    items: windows.filter((w) => w.weekday === d),
  }));

  if (orgs.isLoading) return <CircularProgress />;

  return (
    <Box>
      <FormControl size="small" sx={{ minWidth: 280, mb: 3 }}>
        <InputLabel>{t("activity.selectOrganization")}</InputLabel>
        <Select
          label={t("activity.selectOrganization")}
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        >
          {(orgs.data?.items ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.code} — {o.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {profile.isLoading && <CircularProgress />}
      {profile.data && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              label={t("activity.label")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={!canWrite}
              sx={{ minWidth: 240 }}
            />
            {canWrite && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => applyPreset("24_7")}>
                  {t("activity.preset24_7")}
                </Button>
                <Button size="small" variant="outlined" onClick={() => applyPreset("weekdays")}>
                  {t("activity.presetWeekdays")}
                </Button>
                <Button size="small" variant="outlined" onClick={() => applyPreset("weekend")}>
                  {t("activity.presetWeekendOnly")}
                </Button>
                <Button size="small" variant="outlined" color="error" onClick={() => applyPreset("clear")}>
                  {t("activity.presetClear")}
                </Button>
              </Stack>
            )}
          </Stack>

          {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
          {savedMessage && <Alert severity="success" sx={{ mb: 2 }}>{savedMessage}</Alert>}

          <Stack spacing={1.5}>
            {windowsByDay.map(({ day, items }) => (
              <Card key={day} variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Box sx={{ minWidth: 110 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(`activity.weekday${day}`)}
                      </Typography>
                      {items.length === 0 && (
                        <Chip size="small" variant="outlined" label={t("activity.closed")} sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flex: 1, flexWrap: "wrap", gap: 1 }}
                    >
                      {items.map((w) => (
                        <Stack
                          key={w._key}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ p: 0.5, border: 1, borderColor: "divider", borderRadius: 1 }}
                        >
                          <TextField
                            size="small"
                            type="time"
                            value={w.start_time}
                            onChange={(e) => {
                              setWindows((prev) =>
                                prev.map((x) =>
                                  x._key === w._key ? { ...x, start_time: e.target.value } : x,
                                ),
                              );
                            }}
                            disabled={!canWrite}
                            sx={{ width: 110 }}
                          />
                          <Typography variant="body2" color="text.secondary">→</Typography>
                          <TextField
                            size="small"
                            type="time"
                            value={w.end_time}
                            onChange={(e) => {
                              setWindows((prev) =>
                                prev.map((x) =>
                                  x._key === w._key ? { ...x, end_time: e.target.value } : x,
                                ),
                              );
                            }}
                            disabled={!canWrite}
                            sx={{ width: 110 }}
                          />
                          {canWrite && (
                            <Tooltip title="Retirer">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setWindows((prev) => prev.filter((x) => x._key !== w._key))
                                }
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      ))}
                      {canWrite && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            setWindows((prev) => [
                              ...prev,
                              { weekday: day, start_time: "08:00", end_time: "18:00", _key: makeKey() },
                            ])
                          }
                        >
                          {t("activity.addWindow")}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {canWrite && (
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {t("activity.save")}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
